/**
 * ErpImportBreakdown — the four-bucket partition the THREE ERP renderers share
 * (TV1-2294 ticket 48).
 *
 * ── WHY THIS FILE EXISTS ─────────────────────────────────────────────────────
 * `ErpItemImportRenderer`, `ErpStockImportRenderer` and `ErpPriceImportRenderer`
 * each carried a byte-identical copy of this block: the same four entries, the
 * same two selector calls, the same `Group gap="lg"` + `Stack gap={2}` markup,
 * the same two testids. Three hand-maintained copies of one thing is the exact
 * shape of every parity gap this PR's ticket-18 audit found, and the mobile
 * layout change below would otherwise have had to be applied — and kept correct
 * — three times.
 *
 * ── WHY IT IS A FUNCTION, NOT A COMPONENT ────────────────────────────────────
 * The three ERP renderers are covered by NODE-MODE oracles
 * (`Erp*Renderer.test.ts`): the web-app's vitest runs with `environment: 'node'`,
 * so those tests call the renderer as a plain function and WALK the returned
 * element tree, matching on `batchProcessesTestIds`. A child COMPONENT would
 * appear in that tree as an unrendered `<ErpImportBreakdown />` element whose
 * internals the walker cannot see, so every breakdown assertion in all three
 * files would start returning `undefined` — six tests turning green-to-red for a
 * refactor that changes no behaviour, or (worse) being "fixed" by weakening them.
 *
 * Returning the element tree from a plain function keeps it INLINE in the
 * caller's tree, so the oracles keep working untouched. That is also why it takes
 * `t` as an argument instead of calling `useTranslation`: the renderers are
 * hook-free by design and must stay callable as plain functions.
 *
 * ── THE COUNTS STILL GO THROUGH THE SHARED SELECTORS ─────────────────────────
 * `successCount` / `errorCount` from `shared/state/progressSelectors`, never the
 * raw row fields. Each original file documented at length WHY (only
 * `markCompleted` writes the raw counters, so an ERRORED run — the one an
 * operator is most likely to open — has none of them, and reading them renders
 * 0/0/0/0 directly under a bar showing the real number). Consolidating means
 * that rule is now enforced in ONE place instead of relying on three copies
 * staying honest.
 */

import type { ReactElement } from 'react';
import { Group, Stack, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { batchProcessesTestIds } from '@domains/batchProcesses/testids';
import type { ProgressProcess } from '../../../shared/state/progressSelectors';
import { errorCount, successCount } from '../../../shared/state/progressSelectors';
import classes from './OperationRenderers.module.css';

export type ErpBreakdownEntry = {
  key: string;
  labelKey: string;
  fallback: string;
  value: number;
  color?: string;
};

/**
 * The partition itself. Exported separately so a test (or a future summary line)
 * can assert the NUMBERS without going through the element tree.
 *
 * `projectedCount` is `null` for IMPORTED on purpose: the detail renderer is not
 * handed the batch-progress projection, so success falls back to the write-side
 * `completedCount - errorCount` — precisely the number `recordError` persisted.
 *
 * Caveat, deliberately not papered over: on an errored run the two SKIP buckets
 * are genuinely not yet reported (only the completion event carries them), so
 * they read 0 and the `imported + unchanged + unresolved + errored = totalItems`
 * identity holds only once the run completes.
 */
export const buildErpImportBreakdown = ( counters: ProgressProcess ): ErpBreakdownEntry[] => [
  {
    key: 'imported',
    labelKey: 'batchProcesses_renderer_erpImported',
    fallback: 'Importados',
    value: successCount( counters, null )
  },
  {
    key: 'unchanged',
    labelKey: 'batchProcesses_renderer_erpUnchanged',
    fallback: 'Sin cambios',
    value: counters.noOpCount ?? 0
  },
  {
    key: 'unresolved',
    labelKey: 'batchProcesses_renderer_erpUnresolved',
    fallback: 'Sin identificar',
    value: counters.skippedUnresolvedCount ?? 0
  },
  {
    key: 'errored',
    labelKey: 'batchProcesses_renderer_erpErrored',
    fallback: 'Con error',
    value: errorCount( counters ),
    color: 'var(--mantine-color-red-6)'
  }
];

/**
 * Rendered whenever the run has settled — INCLUDING when every counter but
 * `unchanged` is zero, which is exactly the re-run case the operator must be
 * able to read as "nothing to do", not as "nothing worked".
 *
 * `classes.erpBreakdown` is inert above 768px and turns the wrapping flex row
 * into a 2x2 grid below it (see the stylesheet for the measurement).
 */
export const renderErpImportBreakdown = (
  counters: ProgressProcess,
  t: TFunction
): ReactElement => (
  <Group
    gap="lg"
    className={classes.erpBreakdown}
    data-testid={batchProcessesTestIds.erpImportBreakdown}
  >
    {buildErpImportBreakdown( counters ).map(( entry ) => (
      <Stack key={entry.key} gap={2}>
        <Text
          fz={16}
          fw={600}
          c={entry.color}
          data-testid={batchProcessesTestIds.erpImportBreakdownCount( entry.key )}
        >
          {entry.value}
        </Text>
        <Text fz={11} c="dimmed">
          {t( entry.labelKey, entry.fallback )}
        </Text>
      </Stack>
    ))}
  </Group>
);
